import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ProjectService } from 'src/app/services/project.service';
import { ToastService } from 'src/app/services/toast.service';
import { SignalingTypeEnum } from 'src/enumerators';
import { PdfLine, PdfMarker } from 'src/app/components/pdf-viewer/pdf-viewer.component';

// Pin com letra (A/B) usado durante a edição. Formato de "gota" com a letra
// dentro, ancorado pela ponta inferior no ponto clicado.
function pinSvg(letter: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='36' height='44' viewBox='0 0 36 44'>`
    + `<path d='M18 43 C6 28 2 20 2 14 A16 16 0 0 1 34 14 C34 20 30 28 18 43 Z' fill='%23263238' stroke='white' stroke-width='2'/>`
    + `<circle cx='18' cy='14' r='9' fill='white'/>`
    + `<text x='18' y='19' font-size='12' font-family='Arial, sans-serif' font-weight='bold' fill='%23263238' text-anchor='middle'>${letter}</text>`
    + `</svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}
const PIN_A = pinSvg('A');
const PIN_B = pinSvg('B');

interface ImgPoint { posX: number; posY: number; fileDimensions: string; }

@Component({
  selector: 'app-master-file',
  templateUrl: './master-file.component.html',
  styleUrls: ['./master-file.component.css']
})
export class MasterFileComponent implements OnInit {
  // Variáveis
  @Input() projectId = ''; // Id do Projeto
  @Input() masterFile: any; // Atributo do Arquivo Mestre Dentro da Sinalização
  @Input() signType: any; // Tipo de sinalização
  @Input() selectedFile = '';
  @Input() pdf?: boolean; // override: caller já sabe se é PDF (URL assinada pode não ter extensão)
  @Input() signSvg?: string; // ícone (SVG) da sinalização sendo posicionada
  @Input() signLabel = ''; // nome/código da sinalização sendo posicionada
  // Outras sinalizações já posicionadas no mesmo arquivo-mestre (referência,
  // somente leitura). Cada item: { locations, svg (ícone da sinalização) }.
  @Input() referenceMarkers: Array<{ locations: any; svg: string }> = [];
  @Output() saveFile: EventEmitter<any> = new EventEmitter();

  // PDFs são renderizados via <app-pdf-viewer> (pdfjs-dist em canvas).
  isPdf = false;
  pdfSrc: string | null = null;

  listFiles: Array<{ id: string; name: string; url: string; mimeType?: string }> = [];
  selectedPoint: 'A' | 'B' | '' = ''; // Ponto sendo posicionado
  selectedPointA: ImgPoint = { posX: 0, posY: 0, fileDimensions: '0x0' };
  selectedPointB: ImgPoint = { posX: 0, posY: 0, fileDimensions: '0x0' };
  allowB = false; // ponto B disponível (opcional) — só não-vertical
  imageDims = '0x0';

  // Pins pro template
  pinA = PIN_A;
  pinB = PIN_B;

  // Referência (imagem): markers com svg da sinalização + fileDimensions.
  referenceImagePoints: Array<{ posX: number; posY: number; fileDimensions: string; svg: string }> = [];

  // Posicionamento sobre PDF: coordenadas NORMALIZADAS (0..1) por página.
  pdfLocation: { page: number; pointA: { x: number; y: number } | null; pointB: { x: number; y: number } | null } = {
    page: 1, pointA: null, pointB: null,
  };
  pdfMarkers: PdfMarker[] = [];
  pdfLines: PdfLine[] = [];

  constructor(
    private _projectService: ProjectService,
    private _toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.allowB = this.signType != SignalingTypeEnum.Vertical;
    this.detectPdf();
    this.GetImages();
    this.selectedPoint = 'A';
    if (this.isPdf) {
      this.loadPdfMarkersFromMasterFile();
      this.buildPdfReference();
    }
  }

  selectPoint(point: 'A' | 'B') {
    this.selectedPoint = point;
    // No PDF o destaque do ponto vigente vem do marker; recalcula pra mover o
    // círculo tracejado. Na imagem o [class.active] já reage ao selectedPoint.
    if (this.isPdf) this.rebuildPdfMarkers();
  }

  // Limpa os pontos posicionados e re-emite (permite reposicionar do zero).
  resetPoints() {
    this.selectedPoint = 'A';
    if (this.isPdf) {
      this.pdfLocation.pointA = null;
      this.pdfLocation.pointB = null;
      this.rebuildPdfMarkers();
      this.savePdfMarker();
    } else {
      this.selectedPointA = { posX: 0, posY: 0, fileDimensions: this.imageDims };
      this.selectedPointB = { posX: 0, posY: 0, fileDimensions: this.imageDims };
      this.saveImageMarker();
    }
  }

  // ===== PDF =====
  loadPdfMarkersFromMasterFile() {
    const loc = this.masterFile?.locations?.[0];
    if (loc && (loc.page != null)) {
      this.pdfLocation = {
        page: loc.page,
        pointA: loc.pointA?.x != null ? { x: loc.pointA.x, y: loc.pointA.y } : null,
        pointB: loc.pointB?.x != null ? { x: loc.pointB.x, y: loc.pointB.y } : null,
      };
    }
    this.rebuildPdfMarkers();
  }

  // Markers de outras sinalizações no mesmo arquivo-mestre (referência).
  private buildPdfReference() {
    (this.referenceMarkers || []).forEach(ref => {
      const loc = ref?.locations?.[0];
      if (!loc || loc.page == null) return;
      if (loc.pointA?.x != null && loc.pointB?.x != null) {
        this.pdfLines = [...this.pdfLines, { page: loc.page, x1: loc.pointA.x, y1: loc.pointA.y, x2: loc.pointB.x, y2: loc.pointB.y }];
      }
      if (loc.pointA?.x != null) this._pdfRef.push({ page: loc.page, x: loc.pointA.x, y: loc.pointA.y, svg: ref.svg });
      if (loc.pointB?.x != null) this._pdfRef.push({ page: loc.page, x: loc.pointB.x, y: loc.pointB.y, svg: ref.svg });
    });
    this.rebuildPdfMarkers();
  }
  private _pdfRef: PdfMarker[] = [];

  private rebuildPdfMarkers() {
    const markers: PdfMarker[] = [...this._pdfRef];
    if (this.pdfLocation.pointA) {
      markers.push({ page: this.pdfLocation.page, x: this.pdfLocation.pointA.x, y: this.pdfLocation.pointA.y, svg: this.iconFor('A'), label: this.labelFor('A'), active: this.selectedPoint === 'A' });
    }
    if (this.pdfLocation.pointB) {
      markers.push({ page: this.pdfLocation.page, x: this.pdfLocation.pointB.x, y: this.pdfLocation.pointB.y, svg: this.iconFor('B'), label: this.labelFor('B'), active: this.selectedPoint === 'B' });
    }
    this.pdfMarkers = markers;
    this.rebuildCurrentPdfLine();
  }

  // Ícone do marcador atual: o SVG da sinalização quando disponível; senão pin.
  iconFor(point?: 'A' | 'B'): string {
    if (this.signSvg) return this.signSvg;
    return point === 'B' ? this.pinB : this.pinA;
  }
  // Rótulo (A/B) só aparece quando há ícone da sinalização (o pin já traz a letra).
  labelFor(point: 'A' | 'B'): string | undefined {
    return this.signSvg ? point : undefined;
  }

  private rebuildCurrentPdfLine() {
    // Mantém as linhas de referência e recalcula a linha do ponto atual.
    const refLines = this.pdfLines.filter(l => !(l as any).__current);
    const lines = [...refLines];
    if (this.pdfLocation.pointA && this.pdfLocation.pointB) {
      lines.push({
        page: this.pdfLocation.page,
        x1: this.pdfLocation.pointA.x, y1: this.pdfLocation.pointA.y,
        x2: this.pdfLocation.pointB.x, y2: this.pdfLocation.pointB.y,
        __current: true,
      } as any);
    }
    this.pdfLines = lines;
  }

  onPdfPageClick(event: { page: number; x: number; y: number }) {
    this.pdfLocation.page = event.page;
    if (this.selectedPoint == 'B') {
      this.pdfLocation.pointB = { x: event.x, y: event.y };
    } else {
      this.pdfLocation.pointA = { x: event.x, y: event.y };
    }
    this.rebuildPdfMarkers();
    this.savePdfMarker();
  }

  savePdfMarker() {
    const file = {
      id: this.masterFile?.id,
      projectFileId: this.masterFile?.projectFileId || undefined,
      locations: [{
        page: this.pdfLocation.page,
        pointA: { x: this.pdfLocation.pointA?.x ?? null, y: this.pdfLocation.pointA?.y ?? null },
        pointB: { x: this.pdfLocation.pointB?.x ?? null, y: this.pdfLocation.pointB?.y ?? null },
      }],
    };
    this.saveFile.emit(file);
  }

  // ===== Comum =====
  private detectPdf(mimeType?: string) {
    const url: string = this.selectedFile || '';
    if (this.pdf != null) {
      this.isPdf = this.pdf;
    } else {
      this.isPdf = mimeType
        ? /pdf$/i.test(mimeType)
        : /\.pdf(\?|$)/i.test(url);
    }
    this.pdfSrc = this.isPdf ? url : null;
  }

  // Obter as Imagens do Projeto (lista de candidatos + carregar markers).
  GetImages() {
    this._projectService.getImagesFiles(this.projectId).subscribe(
      (next: Array<any>) => {
        next.forEach(projectFile => {
          this.listFiles.push({
            id: projectFile.projectFileId,
            name: projectFile.file.originalName.split('.').reverse().splice(1).reverse().join('.'),
            url: projectFile.url,
            mimeType: projectFile.file?.mimeType,
          });
        });
        if (this.masterFile) this.GetMarkers();
      },
      () => this._toastService.showError('Ocorreu um erro ao obter arquivos do Projeto.'),
    );
  }

  // Carrega markers existentes.
  GetMarkers() {
    if (this.isPdf) { this.loadPdfMarkersFromMasterFile(); return; }
    const id = this.masterFile.id;
    const file = this.listFiles.find(f => (f.id == id || f.id == this.masterFile.projectFileId));
    if (this.masterFile.locations && file) {
      this.selectedFile = file.url;
      const image = new Image();
      image.src = this.selectedFile;
      image.onload = () => {
        const dims = image.width + 'x' + image.height;
        this.imageDims = dims;
        this.selectedPointA = {
          posX: this.masterFile.locations[0].pointA?.['x'] || 0,
          posY: this.masterFile.locations[0].pointA?.['y'] || 0,
          fileDimensions: dims,
        };
        this.selectedPointB = {
          posX: this.masterFile.locations[0].pointB?.['x'] || 0,
          posY: this.masterFile.locations[0].pointB?.['y'] || 0,
          fileDimensions: dims,
        };
      };
    }
  }

  // Ao carregar a imagem: captura dimensões e monta markers de referência.
  onImageLoad(img: HTMLImageElement) {
    const dims = (img.naturalWidth || img.width) + 'x' + (img.naturalHeight || img.height);
    this.imageDims = dims;
    if (!this.selectedPointA.fileDimensions || this.selectedPointA.fileDimensions === '0x0') {
      this.selectedPointA = { ...this.selectedPointA, fileDimensions: dims };
      this.selectedPointB = { ...this.selectedPointB, fileDimensions: dims };
    }
    this.buildImageReference(dims);
  }

  private buildImageReference(dims: string) {
    const points: Array<{ posX: number; posY: number; fileDimensions: string; svg: string }> = [];
    (this.referenceMarkers || []).forEach(ref => {
      const loc = ref?.locations?.[0];
      if (!loc || loc.page != null) return; // page != null => é PDF, ignora aqui
      if (loc.pointA?.x != null) points.push({ posX: loc.pointA.x, posY: loc.pointA.y, fileDimensions: dims, svg: ref.svg });
      if (loc.pointB?.x != null) points.push({ posX: loc.pointB.x, posY: loc.pointB.y, fileDimensions: dims, svg: ref.svg });
    });
    this.referenceImagePoints = points;
  }

  // Clique na imagem (posiciona o ponto selecionado).
  onImageClick(event: MouseEvent, img: HTMLImageElement) {
    if (!this.selectedPoint) return;
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const natW = img.naturalWidth || img.width;
    const natH = img.naturalHeight || img.height;
    const dims = natW + 'x' + natH;
    this.imageDims = dims;
    const x = ((event.clientX - rect.left) / rect.width) * natW;
    const y = ((event.clientY - rect.top) / rect.height) * natH;
    if (this.selectedPoint == 'B') {
      this.selectedPointB = { posX: x, posY: y, fileDimensions: dims };
    } else {
      this.selectedPointA = { posX: x, posY: y, fileDimensions: dims };
    }
    this.saveImageMarker();
  }

  saveImageMarker() {
    const file = {
      id: this.masterFile?.id,
      projectFileId: this.masterFile?.projectFileId || undefined,
      locations: [{
        pointA: { x: this.selectedPointA.posX || null, y: this.selectedPointA.posY || null },
        pointB: { x: this.selectedPointB.posX || null, y: this.selectedPointB.posY || null },
      }],
    };
    this.saveFile.emit(file);
  }

  // Posição (top/left) do pin sobre o arquivo-mestre (imagem).
  GetMarkerPosition(div: any, marker: any) {
    const c = this.markerCenter(div, marker);
    // Ícone 36x36 centralizado no ponto.
    return `top: ${c.y - 18}px; left: ${c.x - 18}px;`;
  }

  // Centro/ponto do marker em px sobre a div de exibição (sem offset do ícone).
  markerCenter(div: any, marker: any): { x: number; y: number } {
    const divWidth = div.offsetWidth;
    const divHeight = div.offsetHeight;
    const fileWidth = +marker.fileDimensions.split('x')[0];
    const fileHeight = +marker.fileDimensions.split('x')[1];
    const xScale = divWidth / fileWidth;
    const yScale = divHeight / fileHeight;
    let scale = 0, xOffset = 0, yOffset = 0;
    if (xScale > yScale) { scale = xScale; yOffset = (divHeight - fileHeight * scale) / 2; }
    else { scale = yScale; xOffset = (divWidth - fileWidth * scale) / 2; }
    return { x: marker.posX * scale + xOffset, y: marker.posY * scale + yOffset };
  }

  // Linha (SVG, px) ligando A e B na imagem, se ambos existirem.
  imageLine(div: any): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!this.selectedPointA.posX || !this.selectedPointB.posX) return null;
    const a = this.markerCenter(div, this.selectedPointA);
    const b = this.markerCenter(div, this.selectedPointB);
    return { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
  }
}
